import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { generateClient } from 'aws-amplify/api';
import { toaster } from '@/components/ui/toaster';
import { useOrganisationState } from '@/contexts/OrganisationContext';
import { EVENT_SUBSCRIPTION } from '@/graphql/subscription';

interface Event {
  id: string;
  orgId: string;
  parentOrgId?: string;
  type: string;
  entityId: string;
  entityType: string;
  timestamp: string;
  data: any;
  batchId?: string;
}

interface EventBatch {
  batchId: string;
  orgId: string;
  events: Event[];
  batchTimestamp: string;
}

interface EventContextState {
  connected: boolean;
  loading: boolean;
  error: string | null;
  latestEvents: Event[];
}

interface EventContextValue extends EventContextState {}

const EventContext = createContext<EventContextValue>({} as EventContextValue);

interface EventProviderProps {
  children: React.ReactNode;
}

export const EventProvider: React.FC<EventProviderProps> = ({ children }) => {
  const { currentOrganisation } = useOrganisationState();
  const [state, setState] = useState<EventContextState>({
    connected: false,
    loading: false,
    error: null,
    latestEvents: []
  });

  const showToastForEvent = useCallback((event: Event) => {
    if (!event.type.startsWith('notification')) {
      return;
    }

    const eventData = event.data || {};
    const message = eventData.message || 'New notification';
    const title = eventData.title || 'Notification';

    toaster.create({
      title,
      description: message,
      type: event.type === 'notification.error' ? 'error' : 'info'
    });
  }, []);

  useEffect(() => {
    if (!currentOrganisation?.id) {
      setState((prev) => ({
        ...prev,
        connected: false,
        loading: false,
        error: null,
        latestEvents: []
      }));
      return;
    }

    setState((prev) => ({ ...prev, loading: true, error: null }));

    const client = generateClient();

    const subscription = client.graphql({
      query: EVENT_SUBSCRIPTION,
      variables: { orgId: currentOrganisation.id }
    });

    const observable = subscription as any;
    const sub = observable.subscribe({
      next: ({ data }: { data: { onEvent: EventBatch } }) => {
        const eventBatch = data.onEvent;

        // Parse JSON data for each event
        const parsedEvents = eventBatch.events.map((event) => {
          let parsedData = event.data;
          if (typeof event.data === 'string') {
            try {
              parsedData = JSON.parse(event.data);
            } catch (error) {
              console.warn('Failed to parse event data as JSON:', error, event.data);
              parsedData = event.data;
            }
          }

          return {
            ...event,
            data: parsedData
          };
        });

        setState((prev) => ({
          ...prev,
          connected: true,
          loading: false,
          latestEvents: [...parsedEvents, ...prev.latestEvents].slice(0, 100) // Keep last 100 events
        }));

        // Show toasts for notification events
        parsedEvents.forEach(showToastForEvent);
      },
      error: (err: any) => {
        console.error('Event subscription error:', err);
        setState((prev) => ({
          ...prev,
          connected: false,
          loading: false,
          error: err.message || 'Subscription error'
        }));
      }
    });

    setState((prev) => ({ ...prev, connected: true, loading: false }));

    return () => {
      sub.unsubscribe();
      setState((prev) => ({ ...prev, connected: false }));
    };
  }, [currentOrganisation?.id, showToastForEvent]);

  const contextValue = useMemo(
    () => ({
      ...state
    }),
    [state]
  );

  return <EventContext.Provider value={contextValue}>{children}</EventContext.Provider>;
};

export const useEvent = (eventTypes?: string | string[], callback?: (event: Event) => void): Event[] => {
  const context = useContext(EventContext);
  if (!context) {
    throw new Error('useEvent must be used within EventProvider');
  }

  const processedEventIdsRef = useRef<Set<string>>(new Set());

  const filteredEvents = useMemo(() => {
    if (!eventTypes) return context.latestEvents;
    const types = Array.isArray(eventTypes) ? eventTypes : [eventTypes];
    return context.latestEvents.filter((event) => types.includes(event.type));
  }, [context.latestEvents, eventTypes]);

  const stableCallback = useCallback(
    (event: Event) => {
      callback?.(event);
    },
    [callback]
  );

  useEffect(() => {
    if (stableCallback && filteredEvents.length > 0) {
      // Process all new events, not just the latest one
      filteredEvents.forEach((event) => {
        if (!processedEventIdsRef.current.has(event.id)) {
          processedEventIdsRef.current.add(event.id);
          stableCallback(event);
        }
      });

      // Clean up old event IDs to prevent memory leaks (keep last 200)
      if (processedEventIdsRef.current.size > 200) {
        const eventIds = Array.from(processedEventIdsRef.current);
        const toKeep = eventIds.slice(-100);
        processedEventIdsRef.current = new Set(toKeep);
      }
    }
  }, [filteredEvents, stableCallback]);

  return filteredEvents;
};

export type { Event, EventBatch };
